import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ComicFilter from '@/components/feature/ComicFilter';
import ComicGrid from '@/components/base/ComicGrid';
import Pagination from '@/components/base/Pagination';
import { comicCategories, comics } from '@/mocks/comics';
import { defaultFilter, filterComics, sortComics, type FilterState } from '@/utils/comic';

const PAGE_SIZE = 12;

export default function CategoryPage() {
  const { categoryId = 'action' } = useParams();
  const category = comicCategories.find((item) => item.id === categoryId);
  const [filter, setFilter] = useState<FilterState>({ ...defaultFilter, categories: [categoryId] });
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setFilter({ ...defaultFilter, categories: [categoryId] });
    setPage(1);
  }, [categoryId]);

  const results = useMemo(
    () => sortComics(filterComics(comics, filter), filter.sort),
    [filter],
  );

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const pageItems = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const updateFilter = (next: FilterState) => {
    setFilter(next);
    setPage(1);
  };

  const reset = () => updateFilter({ ...defaultFilter, categories: [categoryId] });

  const countInCategory = comics.filter((comic) => comic.categories.includes(categoryId)).length;

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-8">
      <nav className="mb-4 flex items-center gap-1.5 text-xs text-foreground-500">
        <Link to="/" className="transition hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span>分类</span>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">{category?.name ?? '未知分类'}漫画</span>
      </nav>

      <header className="rounded-lg bg-accent-50 p-6">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent-500 text-2xl text-foreground-950">
            <i className={category?.icon ?? 'ri-book-2-line'} />
          </span>
          <div>
            <h1 className="font-heading text-2xl text-foreground-950">
              {category?.name ?? '未知分类'}漫画
            </h1>
            <p className="mt-1 text-sm text-foreground-600">
              {category?.desc} 本分类共收录 {countInCategory} 部优质作品。
            </p>
          </div>
        </div>
      </header>

      <div className="mt-8 flex gap-8">
        <aside className="hidden w-[240px] shrink-0 lg:block">
          <div className="sticky top-20 rounded-lg border border-background-200 bg-background-50 p-4">
            <div className="mb-1 flex items-center justify-between">
              <p className="text-sm font-medium text-foreground-900">筛选条件</p>
              <button
                type="button"
                onClick={reset}
                className="text-xs text-secondary-600 transition hover:text-secondary-700 cursor-pointer"
              >
                重置
              </button>
            </div>
            <ComicFilter value={filter} onChange={updateFilter} showCategory={false} />
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-foreground-600">
              共 <span className="font-medium text-primary-600">{results.length}</span> 部作品
            </p>
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 px-4 py-2 text-sm text-foreground-700 transition hover:border-primary-400 lg:hidden cursor-pointer"
            >
              <i className="ri-filter-3-line" />
              筛选
            </button>
          </div>

          <ComicGrid comics={pageItems} onReset={reset} />
          <Pagination current={page} total={totalPages} onChange={setPage} />
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-foreground-950/40" onClick={() => setDrawerOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-background-50 p-4">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-base font-medium text-foreground-900">筛选条件</p>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <ComicFilter value={filter} onChange={updateFilter} showCategory={false} />
            <div className="sticky bottom-0 mt-4 flex gap-3 bg-background-50 pt-2">
              <button
                type="button"
                onClick={reset}
                className="flex-1 whitespace-nowrap rounded-full border border-background-300 py-2.5 text-sm text-foreground-700 cursor-pointer"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex-1 whitespace-nowrap rounded-full bg-primary-500 py-2.5 text-sm font-medium text-background-50 cursor-pointer"
              >
                查看 {results.length} 部作品
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}